<template>
    <section id="introductions">
        <div class="lucky"
            v-if="couponProperties">
            <h2>{{$t('REG_LUCKY_YOU')}}</h2>
            <p>{{getLuckyYouText}}</p>
        </div>
        <hr v-else class="welcomeDivider">
        <div>
            <div class="py-1 px-5">
                <h2 class="welcomeTitle text-center" v-text="welcomeText"/>
                <ul class="welcomeList">
                    <li>{{$t('ACCOUNT_FORM_WELCOME_TEXT_1')}}</li>
                    <li v-if='getPauseIsCancel'>{{$t('ACCOUNT_FORM_WELCOME_TEXT_4')}}</li>
                    <li>{{$t('ACCOUNT_FORM_WELCOME_TEXT_3')}}</li>
                    <li>{{$t('ACCOUNT_FORM_WELCOME_TEXT_2', {price: price})}}</li>
                </ul>
            </div>

            <!--First Name-->
            <input-field
                class="mb-3"
                v-model="firstName"
                autocomplete="given-name"
                :is-valid="!errorHandler.has('first_name')"
                :message="$t(errorHandler.get('first_name'))"
                :mandatory="true"
                :label="$t('ACCOUNT_FORM_FIRST_NAME')"/>

            <!--Last Name-->
            <input-field
                class="mb-3"
                v-model="lastName"
                autocomplete="family-name"
                :is-valid="!errorHandler.has('last_name')"
                :message="$t(errorHandler.get('last_name'))"
                :mandatory="true"
                :label="$t('ACCOUNT_FORM_LAST_NAME')"/>

            <div class="clear--sm"/>

            <email-address-field
                :should-validate="true"
                :is-valid="!errorHandler.has('email')"
                :message="$t(errorHandler.get('email'))"
                :mandatory="true"
                v-model="email"/>

            <password-field
                :require-strong-password="true"
                :is-valid="!errorHandler.has('password')"
                :message="$t(errorHandler.get('password'))"
                :mandatory="true"
                v-model="password"/>

            <div class="clear--sm"/>

            <checkbox
                v-model="optInMarketing"
                v-if="!isUSA">
                {{ $t('ACCOUNT_FORM_I_WOULD_LIKE') }}
            </checkbox>

            <!-- <checkbox
                v-model="optInThirdPartyMarketing">
                {{ $t('ACCOUNT_FORM_THIRD_PARTY_OPT') }}
            </checkbox> -->

            <div class="px-3" v-if="!getPauseIsCancel">
                <h4 class="pl-6">
                    {{ $t('ACCOUNT_FORM_YOU_CAN_CHANGE') }}
                    <router-link :to="{ name: 'privacy-policy' }" class="link text-xs" target="_blank">
                        {{ $t('ACCOUNT_FORM_PRIVACY_POLICY') }}
                    </router-link>
                </h4>
            </div>

            <!-- Small gap -->
            <div class="clear--sm"></div>

            <!-- Form butto
            <div class="text-center">
                <submit-button
                    @click="submitCurrentStep"
                    color="green"
                    :processing="isFormProcessing">
                    {{ $t('ACCOUNT_FORM_NEXT_STEP') }}
                </submit-button>
            </div>
            -->
        </div>
    </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { SECTION_ACCOUNT } from '@/constants/Registration';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import Checkbox from '@/components/forms/fields/Checkbox';
import InputField from '@/components/forms/fields/InputField';
import RegistrationService from '@/services/users/RegistrationService';
import PasswordField from '@/components/forms/fields/PasswordField';
import EmailAddressField from '@/components/forms/fields/EmailAddressField';
import IncompleteRegistrationService from '@/services/users/incompleteRegistrationService/IncompleteRegistrationService';

export default {
    data: () => {
        return {
            isFormProcessing: false,
            errorHandler: new ErrorResponseHandler(),
        };
    },
    props: {
        enableIncompleteRegistration: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        submitCurrentStep() {
            this.errorHandler.reset();
            this.isFormProcessing = true;

            if (this.enableIncompleteRegistration) {
                setTimeout(() => (new IncompleteRegistrationService()).updateOrCreate(), 0);
            }

            RegistrationService.validateIntroductions()
                .then(() => {
                    this.$store.commit('register/setSectionComplete', SECTION_ACCOUNT);
                    this.$router.push({ name: this.nextRouteName });
                })
                .catch(error => {
                    this.errorHandler.setResponse(error.response.data);
                    this.isFormProcessing = false;
                    this.$scrollTo('#introductions');
                });
        },
    },
    computed: {
        ...mapGetters({
            couponProperties: 'register/getCouponProperties',
            getPauseIsCancel: 'subscription/getPauseIsCancel',
            isCanvasser: 'auth/isCanvasser',
            isUSA: 'localisation/isUSA',
        }),
        welcomeName: {
            get() {
                return this.$store.getters['register/getWelcomeName'];
            },
            set(value) {
                this.$store.commit('register/setWelcomeName', value);
            },
        },
        firstName: {
            get() {
                return this.$store.getters['register/getFirstName'];
            },
            set(value) {
                this.$store.commit('register/setFirstName', value);
            },
        },
        lastName: {
            get() {
                return this.$store.getters['register/getLastName'];
            },
            set(value) {
                this.$store.commit('register/setLastName', value);
            },
        },
        email: {
            get() {
                return this.$store.getters['register/getEmail'];
            },
            set(value) {
                this.$store.commit('register/setEmail', value);
            },
        },
        password: {
            get() {
                return this.$store.getters['register/getPassword'];
            },
            set(value) {
                this.$store.commit('register/setPassword', value);
            },
        },
        optInMarketing: {
            get() {
                return this.$store.getters['register/getOptInMarketing'];
            },
            set(value) {
                this.$store.commit('register/setOptInMarketing', value);
            },
        },
        nextRouteName() {
            return this.isCanvasser ? 'canvasser-register.delivery' : 'register.delivery';
        },
        welcomeText() {
            if (this.isCanvasser) {
                return this.$t('ACCOUNT_FORM_GREAT');
            }
            return this.$t('ACCOUNT_FORM_PLEASED_TO_MEET');
        },
        getLuckyYouText() {
            return this.couponProperties.registration_text_1 || this.$t('REG_TEXT_1_FALLBACK');
        },
        price() {
            const boxPrice = Number(process.env.VUE_APP_BOX_PRICE);
            const shippingPrice = Number(process.env.VUE_APP_DELIVERY_PRICE || 0);
            return this.$globalQueryParams?.dci === '1' ? boxPrice : boxPrice + shippingPrice;
        },
    },
    components: {
        InputField,
        EmailAddressField,
        PasswordField,
        Checkbox,
    },
};
</script>
<style lang="scss" scoped>
.lucky {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: theme('colors.shop.black');
    background-color: theme('colors.grey.100');
    padding: 0.6rem;
    font: FreddiesFlowersTitle;
    h2, p {
        margin: 0.25rem 0;
        padding: 0;
        text-align: center;
    }
    h2 {
        font-size: 25px;
    }
    p {
        font-size: 20px;
        font-weight: 400;
    }
}
.welcomeTitle {
    font-size: 22px;
}
.welcomeText {
    font-size: 20px;
}
.welcomeDivider {
    height: 1px;
    background-color: #C7C7C7;
    border: none;
    margin: 1rem auto;
    width: 80%;
}
.welcomeList {
    font-style: 20px;
    font-weight: lighter;
    margin-bottom: 1rem;
    list-style: unset;
    margin-left: 2rem;
}
.marketingOptIn {
    font-size: 17px;
}
</style>
